import BgLogin from "$root/assets/images/bg_login.jpg";
import SPHERE_Logo from "$root/assets/images/SPHERE_logo.svg";
import { runWithErrorReporting } from "$root/api/error-reporting/report";
import { useUserValue } from "$root/functional-areas/user";
import { authSessionInit, logout } from "$root/functional-areas/user/auth";
import { mfaRepository } from "$root/functional-areas/user/mfa";
import { mfaEnrollmentToken$ } from "$root/functional-areas/session/atom";
import { readAtom } from "@mdotm/shadcn/legacy/reactivity";
import { FormController, rejectOnInvalid } from "$root/third-party-integrations/react-hook-form";
import { isAxiosResponse } from "$root/third-party-integrations/axios";
import { zExt } from "$root/third-party-integrations/zod";
import { qualifier, ToastableError } from "$root/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { AsyncLinkButton, Column, Row, Text } from "@mdotm/shadcn/legacy/components";
import { Form, FormField, SubmitButton, TextInput } from "@mdotm/shadcn/mdotm/components";
import { useAsync } from "@mdotm/utils/promise";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { noop } from "@mdotm/shadcn/legacy/utils";
import { useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import QRCode from "qrcode";

/**
 * Forced TOTP enrollment screen. Rendered (not routed) by the PlatformRouter gate
 * whenever the backend flags the signed-in user `MUST_ENROLL` — see
 * `needsMfaEnrollment`; why the frontend gates at all is D0006. The only actions available to a
 * gated user are the two enrollment calls; once verification succeeds we re-run
 * `authSessionInit`, `/user/info` resolves `ENROLLED`, and the gate clears.
 */
export function MfaEnrollment(): React.JSX.Element {
	const { t } = useTranslation();
	const user = useUserValue();

	const otpForm = useForm({
		defaultValues: { otp: "" },
		resolver: zodResolver(z.object({ otp: zExt.nonEmptyString(t) })),
	});

	// `AssociateSoftwareToken` + QR generation. `useAsync` gives us the loading/error/
	// success state; the effect below kicks it off once on mount (same idiom as App's
	// autologin), and the "Retry" link re-runs it.
	const [runEnrollment, enrollmentState] = useAsync(async (): Promise<{ qrCodeDataURL: string; secret?: string }> => {
		const { otpauthUri, secret } = await mfaRepository.enrollStart();
		return { qrCodeDataURL: await QRCode.toDataURL(otpauthUri, { margin: 0 }), secret };
	});
	const enrollmentRunRef = useUnsafeUpdatedRef(runEnrollment);
	useEffect(() => {
		if (enrollmentState.kind === "initial") {
			enrollmentRunRef.current().catch(noop);
		}
	}, [enrollmentState.kind, enrollmentRunRef]);

	const handleVerify = async ({ otp }: { otp: string }) => {
		await runWithErrorReporting(
			async () => {
				try {
					await mfaRepository.enrollVerify(otp);
					// Promote the held token into a full session: re-hydrate now that the
					// user resolves as `ENROLLED`, which writes `accessToken$` and clears the gate.
					const heldToken = readAtom(mfaEnrollmentToken$);
					if (heldToken) {
						await authSessionInit(heldToken);
					}
				} catch (err) {
					const response = err != null && typeof err === "object" ? (err as { response?: unknown }).response : null;
					// The backend returns 400 when the entered TOTP code is invalid.
					if (isAxiosResponse(response) && response.status === 400) {
						throw new ToastableError("OTP code is invalid. Please try again.", {
							cause: err,
							icon: "Top-menu-user",
						});
					}
					throw new ToastableError(t("SOMETHING_WENT_WRONG"), { cause: err, icon: "Top-menu-user" });
				}
			},
			{ area: "user", attemptedOperation: { message: "mfa enrollment verify" } },
		);
	};

	return (
		<Column
			className="h-full w-full"
			style={{
				backgroundImage: `url(${BgLogin})`,
				backgroundSize: "cover",
				backgroundPosition: "bottom center",
			}}
		>
			<Column flexGrow={1} className="h-full bg-white px-20 ml-auto max-w-xl w-full overflow-y-auto">
				<div className="mt-auto pt-4 mb-16">
					<img src={SPHERE_Logo} className="w-40 h-auto" alt="Sphere Logo" />
				</div>
				<Form noValidate onSubmitAsync={rejectOnInvalid(otpForm, handleVerify)}>
					<Text as="div" type="Title/L/Light">
						Set up two-factor authentication
					</Text>
					<Text as="div" type="Body/L/Book" className="mt-6 mb-7">
						Your account requires two-factor authentication. Scan the QR code with your preferred authenticator app and
						enter the 6-digit code below to finish.
					</Text>
					<Column gap={24}>
						{enrollmentState.kind === "error" ? (
							<Row justifyContent="center">
								<AsyncLinkButton
									data-qualifier="mfaEnrollment-retry"
									onClickAsync={() => runEnrollment().catch(noop)}
									palette="neutral"
									className="font-bold"
									size="x-small"
								>
									Couldn't load the QR code. Retry
								</AsyncLinkButton>
							</Row>
						) : (
							<div className="mx-auto relative">
								<img
									className="w-full h-auto max-w-[196px] pixelated"
									alt="TOTP key"
									src={enrollmentState.kind === "success" ? enrollmentState.result.qrCodeDataURL : undefined}
								/>
							</div>
						)}
						{enrollmentState.kind === "success" && enrollmentState.result.secret && (
							<Text as="div" type="Body/M/Book" className="text-center" color={themeCSSVars.palette_N500}>
								Or enter this key manually:{" "}
								<Text as="span" type="Body/M/Bold">
									{enrollmentState.result.secret}
								</Text>
							</Text>
						)}
						<FormField label={null} error={otpForm.formState.errors.otp?.message} className="w-full">
							{({ id, invalid }) => (
								<FormController
									control={otpForm.control}
									name="otp"
									render={({ field: { onChange, ref, ...controllerProps } }) => (
										<TextInput
											required
											autoComplete="one-time-code"
											inputAppearance={{ className: "text-center" }}
											placeholder="Insert the OTP"
											id={id}
											invalid={invalid}
											disabled={enrollmentState.kind !== "success"}
											onChangeText={onChange}
											innerRef={ref}
											data-qualifier="mfaEnrollment-one-time-code"
											{...controllerProps}
										/>
									)}
								/>
							)}
						</FormField>
						<Row alignItems="center" flexGrow={0} justifyContent="space-between">
							<AsyncLinkButton
								data-qualifier="mfaEnrollment-logout"
								// Nothing to settle on the way out: the gate holds the token out of `accessToken$`
								// on purpose, so a write leaving here could not be authenticated anyway.
								onClickAsync={() => logout({ credentialLost: true })}
								palette="neutral"
								className="font-bold"
								size="x-small"
							>
								Log out
							</AsyncLinkButton>
							<SubmitButton
								disabled={enrollmentState.kind !== "success"}
								data-qualifier={qualifier.component.button("MfaEnrollment/Submit")}
							>
								Continue
							</SubmitButton>
						</Row>
					</Column>
				</Form>
				<Column flexGrow={0} className="mt-auto pt-4 mb-5">
					<Text as="div" type="Body/M/Book" color={themeCSSVars.palette_N500}>
						<div>Signed in as {user.email}</div>
					</Text>
				</Column>
			</Column>
		</Column>
	);
}

export default MfaEnrollment;
